import React from 'react';
import { useSelector } from 'react-redux';
import { useFragment } from 'react-relay/hooks';
import { browserHistory } from 'react-router';

import { Divider, Drawer, List, ListItem, ListItemText } from '@material-ui/core';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import { graphql } from 'babel-plugin-relay/macro';
import classnames from 'classnames';
import { FormattedSimpleMessage } from 'core';
import { filter, get, includes, isFunction, map } from 'lodash';
import { hasAccessDevPlayground } from 'routes';

import { ProvisioningEnum, RolloutEnum } from '@lib/resources/types';
import { selectCurrentMasterSettings } from '@lib/selectors';

import { BadgeViewerCounts } from 'deprecated/fragments';

import { isUsingOutreachExperience } from 'utils/outreachExperience';
import usePermissions from 'hooks/usePermissions';
import { hasAccessToProduct } from 'utils/permissions';

import CopyrightText from 'components/CopyrightText';
import { makeSelectBroadcastsCount } from 'containers/App/selectors';

import { AppSideMenu_Viewer$key } from './__generated__/AppSideMenu_Viewer.graphql';
import {
  ACTIVITY_FEED_PROVISONING,
  OUTBOUND_REFERRAL_PROVISIONING,
  REFERRAL_PROVISIONING,
  SCHEDULE_PROVISIONING,
} from './constants';
import Icon from './Icon';
import ListItemBadge from './ListItemBadge';
import messages from './messages';

export const drawerWidth = 216;

const styles = ({
  custom: {
    palette: { colorSupportPowder, colorSupportSnow, colorSupportSlate },
  },
  palette: {
    common: { white, black },
    primary,
    secondary,
  },
  spacing: { unit },
  zIndex: { snackbar },
}: Theme) =>
  createStyles({
    divider: {
      backgroundColor: colorSupportPowder,
      opacity: 1,
    },
    drawer: {
      boxShadow: '2px 2px 4px rgba(78, 97, 112, 0.10)',
      flexShrink: 0,
      width: drawerWidth,
      zIndex: snackbar,
    },
    drawerPaper: {
      backgroundColor: white,
      border: 'none',
      position: 'relative',
      width: drawerWidth,
      '&$settings': {
        backgroundColor: primary.main,
      },
    },
    grow: {
      flexGrow: 1,
    },
    item: {
      borderLeft: '4px solid transparent',
      color: black,
      paddingLeft: unit * 3 - 4,
      '& i, & .material-icons, svg': {
        color: 'inherit',
      },
      '&:hover': {
        backgroundColor: `${colorSupportSnow} !important`,
      },
      '&$settings': {
        border: 'none',
        color: white,
        paddingLeft: unit * 3,
        '&:hover': {
          backgroundColor: `${colorSupportSlate} !important`,
        },
      },
      '&:focus': {
        backgroundColor: 'initial',
      },
    },
    itemSelected: {
      backgroundColor: `${white} !important`,
      borderLeft: `4px solid ${secondary.main}`,
      color: secondary.main,
      '&$settings': {
        border: 'none',
        color: white,
        backgroundColor: `${colorSupportSlate} !important`,
        '&:hover': {
          backgroundColor: `${colorSupportSlate} !important`,
        },
      },
      '&:focus': {
        backgroundColor: 'initial',
      },
      '&:hover': {
        backgroundColor: `${colorSupportSnow} !important`,
      },
      '& i, & .material-icons, svg': {
        color: `${secondary.main}`,
      },
    },
    itemButton: {},
    list: {
      padding: `${unit}px 0px`,
    },
    listItemText: {
      color: 'inherit',
      fontSize: 14,
      fontWeight: 500,
      lineHeight: '20px',
    },
    listRoot: {
      padding: `0 0 0 ${unit * 2}px`,
    },
    listItemTextWithBadge: {
      width: 140,
    },
    settings: {},
    iconUnselected: {
      color: `${primary.main} !important`,
    },
  });

const sections = [
  {
    showDivider: false,
    isVisible: (permissions: any, pathname: string) => !/settings/.test(pathname),
    items: [
      {
        icon: 'fi-results',
        text: messages.activityFeed,
        id: 'activity-feed-menu-option',
        pathname: '/',
        divider: false,
        isVisible: (permissions: any, pathname: string, hasOnlyTelehealth: boolean) =>
          hasAccessToProduct(permissions, ACTIVITY_FEED_PROVISONING) && !hasOnlyTelehealth,
      },
      {
        icon: 'fi-calendar',
        text: messages.schedule,
        id: 'schedule-menu-option',
        pathname: '/schedule',
        pathRegex: /^\/schedule/,
        divider: true,
        isVisible: (permissions: any) => hasAccessToProduct(permissions, SCHEDULE_PROVISIONING),
      },
    ],
  },
  {
    showDivider: true,
    isVisible: (permissions: any, pathname: string) => !/settings/.test(pathname),
    items: [
      {
        icon: 'forum',
        text: messages.hub,
        id: 'hub-menu-option',
        pathname: '/hub',
        pathRegex: /^\/hub/,
        isVisible: (permissions: any) => hasAccessToProduct(permissions, ProvisioningEnum.Chat),
      },
      {
        icon: 'rss_feed',
        text: messages.broadcast,
        id: 'broadcasts-menu-option',
        pathname: '/broadcasts',
        pathRegex: /^\/broadcasts/,
        countProp: 'broadcastsCount',
        isVisible: (permissions: any) =>
          hasAccessToProduct(permissions, ProvisioningEnum.Broadcast) &&
          !hasAccessToProduct(permissions, RolloutEnum.OldBroadcasts),
      },
      {
        icon: 'rss_feed',
        text: messages.broadcast,
        id: 'broadcasts-menu-option',
        pathname: '/v1/broadcasts',
        pathRegex: /^\/v1\/broadcasts/,
        countProp: 'broadcastsCount',
        isVisible: (permissions: any) =>
          hasAccessToProduct(permissions, ProvisioningEnum.Broadcast) &&
          hasAccessToProduct(permissions, RolloutEnum.OldBroadcasts),
      },
    ],
  },
  {
    showDivider: true,
    isVisible: (permissions: any, pathname: string) => !/settings/.test(pathname),
    items: [
      {
        icon: 'person_add',
        text: messages.inboundReferrals,
        id: 'inbound-referrals-menu-option',
        pathname: '/inbound-referrals',
        pathRegex: /^\/inbound-referrals/,
        isVisible: (permissions: any, _: any, __: any, isToUseOutreachExperience: boolean) =>
          !isToUseOutreachExperience && hasAccessToProduct(permissions, REFERRAL_PROVISIONING),
      },
      {
        icon: 'person_add',
        text: messages.outreach,
        pathname: '/outreach',
        id: 'outreach-menu-option',
        pathRegex: /^\/outreach/,
        isVisible: (permissions: any, _: any, __: any, isToUseOutreachExperience: boolean) =>
          isToUseOutreachExperience && hasAccessToProduct(permissions, REFERRAL_PROVISIONING),
      },
      {
        icon: 'redo',
        text: messages.outboundReferrals,
        pathname: '/outbound-referrals',
        id: 'outbound-referrals-menu-option',
        pathRegex: /^\/outbound-referrals/,
        isVisible: (permissions: any, _: any, __: any, isToUseOutreachExperience: boolean) =>
          hasAccessToProduct(permissions, OUTBOUND_REFERRAL_PROVISIONING),
      },
      {
        icon: 'av_timer',
        text: messages.waitlists,
        id: 'waitlists-menu-option',
        pathname: '/waitlists',
        pathRegex: /^\/waitlists/,
        isVisible: (permissions: any) => hasAccessToProduct(permissions, ProvisioningEnum.Waitlist),
      },
      {
        icon: 'phone',
        text: messages.recalls,
        id: 'recalls-menu-option',
        pathname: '/recalls',
        pathRegex: /^\/recalls/,
        isVisible: (permissions: any) => hasAccessToProduct(permissions, [ProvisioningEnum.Recall]),
      },
      {
        icon: 'insert_chart',
        text: messages.reports,
        id: 'reports-menu-option',
        pathname: '/reports/_',
        pathRegex: /^\/reports/,
        isVisible: (permissions: any, pathname: string, hasOnlyTelehealth: boolean) => !hasOnlyTelehealth,
      },
      {
        icon: 'monetization_on',
        text: messages.patientBilling,
        id: 'patient-billing-menu-option',
        pathname: '/lumapay/summary',
        pathRegex: /^\/lumapay\/summary/,
        isVisible: (permissions: any) =>
          hasAccessToProduct(permissions, [ProvisioningEnum.LumaPay, ProvisioningEnum.InsuranceVerification]),
      },
    ],
  },
  {
    // labs
    showDivider: true,
    isVisible: (permissions: any, pathname: string) => !/settings/.test(pathname) && permissions.hasAccessToLabs,
    items: [
      {
        icon: 'IconExperiment',
        text: 'Cancellation Prediction',
        id: 'cancellation-prediction-menu-option',
        pathname: '/labs/cancellation-prediction',
      },
      {
        icon: 'IconExperiment',
        text: 'Order a Ride',
        id: 'order-a-ride-menu-option',
        pathname: '/labs/order-ride',
      },
    ],
  },
  {
    // DEV playground
    showDivider: true,
    isVisible: () => hasAccessDevPlayground(),
    items: [
      {
        icon: 'code',
        text: 'DEV Playground',
        id: 'dev-playground-menu-option',
        pathname: '/dev/test',
      },
    ],
  },
];

export interface AppSideMenuProps extends WithStyles<typeof styles> {
  fragmentRefViewer: AppSideMenu_Viewer$key;
  pathname: string;
}

const AppSideMenu: React.FC<AppSideMenuProps> = ({ classes, fragmentRefViewer, pathname }) => {
  const viewer = useFragment<AppSideMenu_Viewer$key>(
    graphql`
      fragment AppSideMenu_Viewer on User {
        ...BadgeViewerCounts_Viewer
      }
    `,
    fragmentRefViewer,
  );
  const broadcastsCount = useSelector(makeSelectBroadcastsCount()) || 0;

  const { permissions, hasAccessToPathname } = usePermissions();
  const settings = useSelector(selectCurrentMasterSettings);
  const isToUseOutreachExperience = isUsingOutreachExperience(settings);

  const isSectionVisible = (section: any) => !isFunction(section.isVisible) || section.isVisible(permissions, pathname);

  const isItemVisible = (item: any) => {
    const res = hasAccessToPathname(item.pathname);
    const provisioning = get(permissions, 'provisioning', []);
    const hasOnlyTelehealth = includes(provisioning, ProvisioningEnum.Telehealth) && provisioning.length === 1;
    return isFunction(item.isVisible)
      ? item.isVisible(permissions, pathname, hasOnlyTelehealth, isToUseOutreachExperience)
      : res;
  };

  const renderMenuItem = (menuItem: any, index: number) => {
    const { icon, text, pathRegex, countProp, id } = menuItem;
    const selected = pathRegex ? pathRegex.test(pathname) : pathname === menuItem.pathname;

    // count can be -1, this is when theres no new message on my chats tab, but still have messages on unassigned tab
    let count = 0;
    if (countProp === 'broadcastsCount') {
      count = broadcastsCount;
    }

    const isSettings = /settings/.test(menuItem.pathname);

    return (
      <ListItem
        classes={{
          root: classnames(classes.item, { [classes.settings]: isSettings }),
          selected: classes.itemSelected,
          button: classnames(classes.itemButton, { [classes.settings]: isSettings }),
        }}
        disableTouchRipple
        key={index}
        selected={selected}
        button
        id={id}
        onClick={() => browserHistory.push(menuItem.pathname)}
      >
        {menuItem.pathname === '/hub' ? (
          <BadgeViewerCounts fragmentRef={viewer} color="secondary" variant="standard-right">
            {icon && (
              <Icon
                classes={{
                  muiIcon: classnames({ [classes.iconUnselected]: !selected }),
                }}
                icon={icon}
              />
            )}
            <ListItemText
              classes={{
                root: classes.listItemTextWithBadge,
                primary: classes.listItemText,
              }}
              primary={typeof text === 'object' ? <FormattedSimpleMessage {...text} /> : text}
            />
          </BadgeViewerCounts>
        ) : (
          <ListItemBadge badgeContent={count === -1 ? '●' : `${count}`} selected={selected}>
            {icon && (
              <Icon
                classes={{
                  fiIcon: classnames({ [classes.iconUnselected]: !selected }),
                  muiIcon: classnames({ [classes.iconUnselected]: !selected }),
                  svgIcon: classnames({ [classes.iconUnselected]: !selected }),
                }}
                icon={icon}
              />
            )}
            <ListItemText
              classes={{
                root: classes.listRoot,
                primary: classnames(classes.listItemText, { [classes.settings]: isSettings }),
              }}
              primary={typeof text === 'object' ? <FormattedSimpleMessage {...text} /> : text}
            />
          </ListItemBadge>
        )}
      </ListItem>
    );
  };

  const renderMenuSection = (section: any, index: number) => {
    if (!isSectionVisible(section)) return;

    const { showDivider, items } = section;
    const itemsVisible = filter(items, isItemVisible);

    if (!itemsVisible.length) return;

    return (
      <React.Fragment key={index}>
        {showDivider && <Divider className={classes.divider} />}
        <List className={classes.list}>{map(itemsVisible, renderMenuItem)}</List>
      </React.Fragment>
    );
  };

  const isSettings = /settings/.test(pathname);

  return (
    <Drawer
      classes={{
        docked: classnames('no-print', classes.drawer),
        paper: classnames(classes.drawerPaper, { [classes.settings]: isSettings }),
      }}
      variant="permanent"
      anchor="left"
    >
      <div className={classes.grow}>{map(sections, renderMenuSection)}</div>
      <CopyrightText />
    </Drawer>
  );
};

export default withStyles(styles)(AppSideMenu);
